/**
 * Mounted once, near the top of the app. Renders nothing until the build we're
 * running has been superseded, then offers a reload.
 *
 * The first check runs at mount and reloads on its own — at that point the page
 * is a blank slate and there is nothing to lose. Every later check only raises
 * the banner: by then somebody may be halfway through a quotation, and throwing
 * that away to save them a refresh is not a trade worth making.
 *
 * See src/lib/versionCheck.js for the fail-open rules and the reload guard.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import {
  VERSION_POLL_INTERVAL_MS,
  checkVersion,
  reloadForVersion,
} from '../../lib/versionCheck';

export const VersionGate = () => {
  const [staleVersion, setStaleVersion] = useState('');
  const dismissedRef = useRef('');

  const report = useCallback((version) => {
    // A version they've already waved away stays away until it changes again.
    if (!version || version === dismissedRef.current) return;
    setStaleVersion(version);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();

    const run = async (auto) => {
      const version = await checkVersion({
        auto,
        // The mount check must not be swallowed by the poll throttle — a tab
        // opened seconds after another one still needs its own answer.
        force: auto,
        signal: controller.signal,
      });
      if (!cancelled) report(version);
    };

    // Page load: reload without asking.
    run(true);

    const interval = setInterval(() => run(false), VERSION_POLL_INTERVAL_MS);

    // Coming back to a tab left open for hours is the most likely moment for
    // this to have gone stale, and the cheapest moment to find out.
    const onVisible = () => {
      if (document.visibilityState === 'visible') run(false);
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);

    return () => {
      cancelled = true;
      controller.abort();
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [report]);

  if (!staleVersion) return null;

  const onReload = async () => {
    const reloading = await reloadForVersion(staleVersion);
    if (!reloading) {
      // Two reloads already failed to shift it, so something between us and the
      // server is pinning the old index.html. Say so rather than looping.
      window.alert(
        'A new version is available but your browser keeps loading the old one.\n\n' +
        'Please force refresh: Ctrl + Shift + R (Windows) or Cmd + Shift + R (Mac).'
      );
    }
  };

  const onDismiss = () => {
    dismissedRef.current = staleVersion;
    setStaleVersion('');
  };

  return (
    <div className="fixed inset-x-0 top-0 z-[9999] flex justify-center px-3 pt-3 pointer-events-none">
      <div
        role="status"
        className="pointer-events-auto flex w-full max-w-xl items-center gap-3 rounded-lg border border-blue-200 bg-blue-50 px-4 py-2.5 shadow-lg dark:border-blue-900 dark:bg-blue-950"
      >
        <span className="flex-1 text-sm text-blue-900 dark:text-blue-100">
          A new version of the app is available.
        </span>
        <button
          type="button"
          onClick={onReload}
          className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-white hover:bg-primary/90"
        >
          Reload
        </button>
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss update notice"
          className="rounded-md px-2 py-1.5 text-sm text-blue-700 hover:bg-blue-100 dark:text-blue-200 dark:hover:bg-blue-900"
        >
          Later
        </button>
      </div>
    </div>
  );
};

export default VersionGate;
